import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useLang, LangToggle } from '../LanguageContext';
import { t } from '../i18n';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── MODAL APERÇU ─────────────────────────────────────────────────────────────
function ModalApercu({ type, data, onClose, onDownload }) {
  if (!data) return null;
  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.box} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <div style={modalStyles.headerTitle}>
            {type === 'pdf' ? '📄 Aperçu PDF' : '📊 Aperçu CSV'}
          </div>
          <button style={modalStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={modalStyles.body}>
          {type === 'pdf' && (
            <>
              <div style={modalStyles.section}>
                <div style={modalStyles.sectionTitle}>KDPeinture — Rapport Commercial</div>
                <div style={modalStyles.meta}>Généré le {data.dateStr} · Période : {data.periodeLabel}</div>
              </div>

              <div style={modalStyles.kpiRow}>
                {data.kpis.map((k, i) => (
                  <div key={i} style={{ ...modalStyles.kpiBox, color: k.color }}>
                    <div style={modalStyles.kpiVal}>{k.val}</div>
                    <div style={modalStyles.kpiLabel}>{k.label}</div>
                  </div>
                ))}
              </div>

              <div style={modalStyles.tableWrap}>
                <div style={modalStyles.tableTitle}>{ t(lang, 'performance_comm') }</div>
                <table style={modalStyles.table}>
                  <thead>
                    <tr style={modalStyles.thead}>
                      {['Commercial', 'Visites', 'Objectif', 'Score', 'Retards', 'Prime'].map(h => (
                        <th key={h} style={modalStyles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.statsComm.length === 0 && (
                      <tr><td colSpan={6} style={{ textAlign: 'center', padding: 8, fontSize: 12, color: '#888' }}>Aucune donnée</td></tr>
                    )}
                    {data.statsComm.map((c, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8f8f8' }}>
                        <td style={modalStyles.td}>{c.prenom} {c.nom}</td>
                        <td style={{ ...modalStyles.td, textAlign: 'center' }}>{c.visites}</td>
                        <td style={{ ...modalStyles.td, textAlign: 'center' }}>{c.objectifVisites || 60}</td>
                        <td style={{ ...modalStyles.td, textAlign: 'center', color: c.score >= 80 ? '#3B6D11' : c.score >= 50 ? '#854F0B' : '#C0392B', fontWeight: 600 }}>{c.score}%</td>
                        <td style={{ ...modalStyles.td, textAlign: 'center', color: '#C0392B' }}>{c.actionsRetard}</td>
                        <td style={{ ...modalStyles.td, textAlign: 'right', color: '#3B6D11', fontWeight: 600 }}>{c.primeEstimee.toLocaleString()} DA</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {data.resultats.length > 0 && (
                <div style={modalStyles.tableWrap}>
                  <div style={modalStyles.tableTitle}>{ t(lang, 'resultats_visites') }</div>
                  <table style={modalStyles.table}>
                    <thead>
                      <tr style={modalStyles.thead}>
                        {['Résultat', 'Nombre', '%'].map(h => <th key={h} style={modalStyles.th}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {data.resultats.map(([r, count], i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8f8f8' }}>
                          <td style={modalStyles.td}>{r}</td>
                          <td style={{ ...modalStyles.td, textAlign: 'center' }}>{count}</td>
                          <td style={{ ...modalStyles.td, textAlign: 'center' }}>{Math.round((count / Math.max(data.totalVisites, 1)) * 100)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {type === 'csv' && (
            <div style={modalStyles.tableWrap}>
              <div style={modalStyles.tableTitle}>Aperçu du fichier CSV</div>
              <table style={modalStyles.table}>
                <thead>
                  <tr style={modalStyles.thead}>
                    {data.headers.map(h => <th key={h} style={modalStyles.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 && (
                    <tr><td colSpan={data.headers.length} style={{ textAlign: 'center', padding: 8, fontSize: 12, color: '#888' }}>Aucune donnée</td></tr>
                  )}
                  {data.rows.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8f8f8' }}>
                      {row.map((cell, j) => <td key={j} style={modalStyles.td}>{cell}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
                Format : CSV séparateur point-virgule · Encodage UTF-8
              </div>
            </div>
          )}
        </div>

        <div style={modalStyles.footer}>
          <button style={modalStyles.btnCancel} onClick={onClose}>✕ Fermer</button>
          <button style={modalStyles.btnDownload} onClick={onDownload}>
            ⬇ Télécharger {type === 'pdf' ? 'PDF' : 'CSV'}
          </button>
        </div>
      </div>
    </div>
  );
}

const modalStyles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  box: { background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #eee', flexShrink: 0 },
  headerTitle: { fontSize: 15, fontWeight: 700, color: '#1C2833' },
  closeBtn: { background: '#f0f0f0', border: 'none', borderRadius: '50%', width: 28, height: 28, fontSize: 13, cursor: 'pointer', color: '#555' },
  body: { overflowY: 'auto', padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 },
  section: { background: '#1C2833', borderRadius: 10, padding: '12px 14px' },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: '#fff' },
  meta: { fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 4 },
  kpiRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6 },
  kpiBox: { background: '#f5f5f5', borderRadius: 8, padding: '8px 4px', textAlign: 'center' },
  kpiVal: { fontSize: 18, fontWeight: 700 },
  kpiLabel: { fontSize: 9, color: '#888', marginTop: 2 },
  tableWrap: { display: 'flex', flexDirection: 'column', gap: 6 },
  tableTitle: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 11 },
  thead: { background: '#1C2833' },
  th: { padding: '6px 8px', color: '#fff', textAlign: 'left', fontWeight: 600, fontSize: 10 },
  td: { padding: '6px 8px', borderBottom: '1px solid #f0f0f0', color: '#1C2833' },
  footer: { display: 'flex', gap: 8, padding: '12px 16px', borderTop: '1px solid #eee', flexShrink: 0 },
  btnCancel: { flex: 1, padding: '11px', border: '1px solid #ddd', borderRadius: 10, background: '#fff', color: '#666', fontSize: 13, cursor: 'pointer' },
  btnDownload: { flex: 2, padding: '11px', background: '#1C2833', border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
// ─────────────────────────────────────────────────────────────────────────────

export default function Rapports({ onClose }) {
  const [visites, setVisites] = useState([]);
  const [clients, setClients] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [actions, setActions] = useState([]);
  const [assignations, setAssignations] = useState([]);
  const [objectifs, setObjectifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('mois');
  const [commFiltre, setCommFiltre] = useState('Tous');
  const [modal, setModal] = useState(null);
  const { lang, isRTL } = useLang();

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    setLoading(true);
    try {
      const vSnap = await getDocs(collection(db, 'visites'));
      setVisites(vSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const cSnap = await getDocs(collection(db, 'clients'));
      setClients(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const uSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'commercial')));
      setCommerciaux(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const aSnap = await getDocs(collection(db, 'actions'));
      setActions(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const asSnap = await getDocs(collection(db, 'assignations'));
      setAssignations(asSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const oSnap = await getDocs(collection(db, 'objectifs'));
      setObjectifs(oSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.log(e); }
    setLoading(false);
  };

  const maintenant = new Date();
  const debutPeriode = () => {
    const d = new Date();
    if (periode === 'semaine') { d.setDate(d.getDate() - 7); }
    else if (periode === 'mois') { d.setDate(1); d.setHours(0,0,0,0); }
    else if (periode === 'trimestre') { d.setMonth(d.getMonth() - 3); }
    return d;
  };

  const visitesFiltre = visites.filter(v => {
    if (!v.heureDebut) return false;
    const date = new Date(v.heureDebut);
    const ok = date >= debutPeriode();
    if (!ok) return false;
    if (commFiltre !== 'Tous') return v.commercialId === commFiltre;
    return true;
  });

  const actionsFiltre = actions.filter(a => {
    if (commFiltre !== 'Tous') return a.commercialId === commFiltre;
    return true;
  });

  const actionsRetard = actionsFiltre.filter(a => a.statut === 'A FAIRE' && a.dateAction && new Date(a.dateAction) < maintenant);
  const actionsFaites = actionsFiltre.filter(a => a.statut === 'FAIT');

  const statsComm = commerciaux.map(c => {
    const cvs = visitesFiltre.filter(v => v.commercialId === c.id);
    const acts = actionsFiltre.filter(a => a.commercialId === c.id && a.statut === 'A FAIRE' && a.dateAction && new Date(a.dateAction) < maintenant);
    const assignation = assignations.find(a => a.commercialId === c.id);
    const score = Math.round((cvs.length / Math.max(c.objectifVisites || 60, 1)) * 100);
    const varTotal = assignation?.variableTotal || c.variableTotal || 0;
    const primeEstimee = Math.round(varTotal * Math.min(score, 100) / 100);
    return { ...c, visites: cvs.length, actionsRetard: acts.length, score, primeEstimee, varTotal };
  }).filter(c => commFiltre === 'Tous' || c.id === commFiltre);

  const resultatsMap = {};
  visitesFiltre.forEach(v => {
    const r = v.resultat || 'Non renseigné';
    resultatsMap[r] = (resultatsMap[r] || 0) + 1;
  });
  const resultats = Object.entries(resultatsMap).sort((a, b) => b[1] - a[1]);

  const visitesParJour = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const label = d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });
    const count = visites.filter(v => {
      if (!v.heureDebut) return false;
      const vd = new Date(v.heureDebut);
      return vd.toDateString() === d.toDateString() && (commFiltre === 'Tous' || v.commercialId === commFiltre);
    }).length;
    visitesParJour.push({ label, count });
  }
  const maxVisites = Math.max(...visitesParJour.map(v => v.count), 1);

  const dateStr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  const periodeLabel = periode === 'semaine' ? '7 derniers jours' : periode === 'mois' ? 'Ce mois' : 'Ce trimestre';

  // ── APERÇU CSV ──
  const ouvrirAperçuCSV = () => {
    setModal({
      type: 'csv',
      data: {
        headers: ['Commercial', 'Visites', 'Score', 'Actions retard', 'Prime estimée DA'],
        rows: statsComm.map(c => [`${c.prenom} ${c.nom}`, c.visites, `${c.score}%`, c.actionsRetard, c.primeEstimee]),
      }
    });
  };

  // ── APERÇU PDF ──
  const ouvrirAperçuPDF = () => {
    setModal({
      type: 'pdf',
      data: {
        dateStr,
        periodeLabel,
        kpis: [
          { val: visitesFiltre.length, label: 'Visites', color: '#1C2833' },
          { val: clients.length, label: 'Clients', color: '#185FA5' },
          { val: actionsRetard.length, label: 'Retards', color: '#C0392B' },
          { val: actionsFaites.length, label: 'Actions faites', color: '#3B6D11' },
        ],
        statsComm,
        resultats,
        totalVisites: visitesFiltre.length,
      }
    });
  };

  // ── TÉLÉCHARGEMENT CSV ──
  const telechargerCSV = () => {
    const rows = [
      ['Commercial', 'Visites', 'Score', 'Actions retard', 'Prime estimée DA'],
      ...statsComm.map(c => [`${c.prenom} ${c.nom}`, c.visites, `${c.score}%`, c.actionsRetard, c.primeEstimee])
    ];
    const csv = rows.map(r => r.join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport-${new Date().toLocaleDateString('fr-FR').replace(/\//g, '-')}.csv`;
    a.click();
    setModal(null);
  };

  // ── TÉLÉCHARGEMENT PDF ──
  const telechargerPDF = () => {
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();

      doc.setFillColor(28, 40, 51);
      doc.rect(0, 0, pageW, 28, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('KDPeinture — Rapport Commercial', 14, 11);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Généré le ${dateStr}  ·  Période : ${periodeLabel}`, 14, 19);
      if (commFiltre !== 'Tous') {
        const comm = commerciaux.find(c => c.id === commFiltre);
        if (comm) doc.text(`Commercial : ${comm.prenom} ${comm.nom}`, 14, 25);
      }

      let y = 36;

      doc.setTextColor(136, 136, 136);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('INDICATEURS CLÉS', 14, y);
      y += 5;

      const kpis = [
        { label: 'Visites', val: visitesFiltre.length, color: [28, 40, 51] },
        { label: 'Clients', val: clients.length, color: [24, 95, 165] },
        { label: 'Actions en retard', val: actionsRetard.length, color: [192, 57, 43] },
        { label: 'Actions faites', val: actionsFaites.length, color: [59, 109, 17] },
      ];
      const kpiW = (pageW - 28) / 4;
      kpis.forEach((k, i) => {
        const x = 14 + i * kpiW;
        doc.setFillColor(245, 245, 245);
        doc.roundedRect(x, y, kpiW - 3, 18, 2, 2, 'F');
        doc.setTextColor(...k.color);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(String(k.val), x + (kpiW - 3) / 2, y + 10, { align: 'center' });
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(120, 120, 120);
        doc.text(k.label, x + (kpiW - 3) / 2, y + 15, { align: 'center' });
      });
      y += 25;

      doc.setTextColor(136, 136, 136);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('PERFORMANCE PAR COMMERCIAL', 14, y);
      y += 4;

      autoTable(doc, {
        startY: y,
        head: [['Commercial', 'Visites', 'Objectif', 'Score', 'Retards', 'Prime estimée']],
        body: statsComm.map(c => [
          `${c.prenom || ''} ${c.nom || ''}`.trim(),
          c.visites, c.objectifVisites || 60, `${c.score}%`, c.actionsRetard,
          `${c.primeEstimee.toLocaleString('fr-FR')} DA`,
        ]),
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [28, 40, 51], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 45 }, 1: { halign: 'center' }, 2: { halign: 'center' },
          3: { halign: 'center' }, 4: { halign: 'center', textColor: [192, 57, 43] },
          5: { halign: 'right', fontStyle: 'bold', textColor: [59, 109, 17] },
        },
        alternateRowStyles: { fillColor: [248, 248, 248] },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 3) {
            const score = parseInt(data.cell.raw);
            if (score >= 80) data.cell.styles.textColor = [59, 109, 17];
            else if (score >= 50) data.cell.styles.textColor = [133, 79, 11];
            else data.cell.styles.textColor = [192, 57, 43];
          }
        },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 10;

      if (resultats.length > 0) {
        if (y > 220) { doc.addPage(); y = 20; }
        doc.setTextColor(136, 136, 136);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('RÉSULTATS DES VISITES', 14, y);
        y += 4;
        autoTable(doc, {
          startY: y,
          head: [['Résultat', 'Nombre', 'Part (%)']],
          body: resultats.map(([res, count]) => [res, count, `${Math.round((count / Math.max(visitesFiltre.length, 1)) * 100)}%`]),
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fillColor: [28, 40, 51], textColor: 255, fontStyle: 'bold', fontSize: 8 },
          columnStyles: { 0: { cellWidth: 100 }, 1: { halign: 'center' }, 2: { halign: 'center' } },
          alternateRowStyles: { fillColor: [248, 248, 248] },
          margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      if (y > 220) { doc.addPage(); y = 20; }
      doc.setTextColor(136, 136, 136);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('VISITES — 7 DERNIERS JOURS', 14, y);
      y += 4;
      autoTable(doc, {
        startY: y,
        head: [visitesParJour.map(v => v.label)],
        body: [visitesParJour.map(v => v.count)],
        styles: { fontSize: 9, cellPadding: 3, halign: 'center' },
        headStyles: { fillColor: [240, 240, 240], textColor: [100, 100, 100], fontStyle: 'normal', fontSize: 8 },
        bodyStyles: { fontStyle: 'bold', textColor: [28, 40, 51] },
        margin: { left: 14, right: 14 },
      });

      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(180, 180, 180);
        doc.text(`KDPeinture · Rapport Commercial · ${dateStr} · Page ${i}/${totalPages}`,
          pageW / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
      }

      doc.save(`rapport-kdpeinture-${new Date().toLocaleDateString('fr-FR').replace(/\//g, '-')}.pdf`);
    } catch (err) {
      console.error('Erreur export PDF:', err);
      alert('Erreur lors de la génération du PDF.');
    }
    setModal(null);
  };

  const COULEURS = ['#C0392B', '#185FA5', '#3B6D11', '#854F0B', '#1D9E75'];

  return (
    <div style={styles.app}>
      {modal && (
        <ModalApercu
          type={modal.type}
          data={modal.data}
          onClose={() => setModal(null)}
          onDownload={modal.type === 'pdf' ? telechargerPDF : telechargerCSV}
        />
      )}

      <div style={styles.topbar}>
        <button onClick={onClose} style={styles.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <div style={styles.topbarTitle}>Rapports</div>
          <div style={styles.topbarSub}>{new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <LangToggle />
          <button style={styles.exportBtn} onClick={ouvrirAperçuCSV}>📥 CSV</button>
          <button style={{ ...styles.exportBtn, background: 'rgba(192,57,43,0.85)' }} onClick={ouvrirAperçuPDF}>📄 PDF</button>
        </div>
      </div>

      <div style={styles.filtreBar}>
        <div style={styles.filtreRow}>
          <div style={styles.filtreGroup}>
            {['semaine', 'mois', 'trimestre'].map(p => (
              <button key={p} style={{ ...styles.filtreBtn, ...(periode === p ? styles.filtreBtnActive : {}) }}
                onClick={() => setPeriode(p)}>
                {p === 'semaine' ? '7 jours' : p === 'mois' ? 'Ce mois' : 'Trimestre'}
              </button>
            ))}
          </div>
          <select style={styles.filtreSelect} value={commFiltre} onChange={e => setCommFiltre(e.target.value)}>
            <option value="Tous">Tous</option>
            {commerciaux.map(c => <option key={c.id} value={c.id}>{c.prenom}</option>)}
          </select>
        </div>
      </div>

      <div style={styles.content}>
        <div style={styles.kpisGrid}>
          {[
            { val: visitesFiltre.length, label: 'Visites', color: '#1C2833', icon: '🚶' },
            { val: clients.length, label: 'Clients', color: '#185FA5', icon: '👥' },
            { val: actionsRetard.length, label: 'Retards', color: '#C0392B', icon: '⚠️' },
            { val: actionsFaites.length, label: 'Actions faites', color: '#3B6D11', icon: '✅' },
          ].map((k, i) => (
            <div key={i} style={styles.kpiCard}>
              <div style={styles.kpiIcon}>{k.icon}</div>
              <div style={{ ...styles.kpiVal, color: k.color }}>{k.val}</div>
              <div style={styles.kpiLabel}>{k.label}</div>
            </div>
          ))}
        </div>

        <div style={styles.sectionLabel}>{t(lang, 'visite_7j')}</div>
        <div style={styles.graphCard}>
          <div style={styles.barChart}>
            {visitesParJour.map((v, i) => (
              <div key={i} style={styles.barItem}>
                <div style={styles.barWrap}>
                  <div style={{ ...styles.bar, height: `${Math.round((v.count / maxVisites) * 100)}%`, background: v.count > 0 ? '#1C2833' : '#e0e0e0' }}>
                    {v.count > 0 && <div style={styles.barVal}>{v.count}</div>}
                  </div>
                </div>
                <div style={styles.barLabel}>{v.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.sectionLabel}>{ t(lang, 'performance_comm') }</div>
        {loading && <div style={styles.empty}>Chargement...</div>}
        {statsComm.map((c, i) => (
          <div key={c.id} style={styles.commCard}>
            <div style={styles.commHeader}>
              <div style={{ ...styles.commAv, background: [['#FCEBEB', '#C0392B'], ['#E6F1FB', '#185FA5'], ['#EAF3DE', '#3B6D11']][i % 3][0], color: [['#FCEBEB', '#C0392B'], ['#E6F1FB', '#185FA5'], ['#EAF3DE', '#3B6D11']][i % 3][1] }}>
                {(c.prenom || 'C')[0]}
              </div>
              <div style={{ flex: 1 }}>
                <div style={styles.commNom}>{c.prenom} {c.nom}</div>
                <div style={styles.commSub}>{c.visites} visite(s) · {c.actionsRetard} retard(s)</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: c.score >= 80 ? '#3B6D11' : c.score >= 50 ? '#854F0B' : '#C0392B' }}>{c.score}%</div>
                <div style={{ fontSize: 10, color: '#888' }}>score</div>
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              {[{ label: 'Visites', val: c.visites, max: c.objectifVisites || 60, pct: Math.round((c.visites / Math.max(c.objectifVisites || 60, 1)) * 100) }].map((k, j) => (
                <div key={j}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888', marginBottom: 3 }}>
                    <span>{k.label}</span><span>{k.val} / {k.max}</span>
                  </div>
                  <div style={{ height: 5, background: '#f0f0f0', borderRadius: 99, marginBottom: 8 }}>
                    <div style={{ height: 5, background: k.pct >= 80 ? '#3B6D11' : k.pct >= 50 ? '#E67E22' : '#C0392B', borderRadius: 99, width: `${Math.min(k.pct, 100)}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
            <div style={styles.primeRow}>
              <span style={styles.primeLabel}>Prime estimée ce mois</span>
              <span style={styles.primeVal}>{c.primeEstimee.toLocaleString()} DA</span>
            </div>
          </div>
        ))}

        {resultats.length > 0 && (
          <>
            <div style={styles.sectionLabel}>{ t(lang, 'resultats_visites') }</div>
            <div style={styles.resultatCard}>
              {resultats.map(([resultat, count], i) => (
                <div key={i} style={styles.resultatRow}>
                  <div style={{ ...styles.resultatDot, background: COULEURS[i % COULEURS.length] }}></div>
                  <div style={{ flex: 1, fontSize: 13, color: '#1C2833' }}>{resultat}</div>
                  <div style={styles.resultatCount}>{count}</div>
                  <div style={styles.resultatBarWrap}>
                    <div style={{ ...styles.resultatBar, width: `${Math.round((count / visitesFiltre.length) * 100)}%`, background: COULEURS[i % COULEURS.length] }}></div>
                  </div>
                  <div style={styles.resultatPct}>{Math.round((count / visitesFiltre.length) * 100)}%</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' },
  topbar: { background: '#1C2833', padding: '1rem 1.25rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  backBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  topbarTitle: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 11, opacity: 0.7, marginTop: 2 },
  exportBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, padding: '6px 10px', cursor: 'pointer', flexShrink: 0 },
  filtreBar: { background: '#fff', padding: '8px 12px', borderBottom: '1px solid #eee', flexShrink: 0 },
  filtreRow: { display: 'flex', gap: 8, alignItems: 'center' },
  filtreGroup: { display: 'flex', gap: 4, flex: 1 },
  filtreBtn: { flex: 1, padding: '7px 4px', border: '1px solid #ddd', borderRadius: 8, background: '#f9f9f9', color: '#666', fontSize: 11, cursor: 'pointer' },
  filtreBtnActive: { background: '#1C2833', border: '1px solid #1C2833', color: '#fff', fontWeight: 600 },
  filtreSelect: { padding: '7px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 12, background: '#f9f9f9' },
  content: { flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 10 },
  sectionLabel: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 },
  kpisGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6 },
  kpiCard: { background: '#fff', borderRadius: 10, padding: '10px 8px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  kpiIcon: { fontSize: 18, marginBottom: 4 },
  kpiVal: { fontSize: 20, fontWeight: 700 },
  kpiLabel: { fontSize: 10, color: '#888', marginTop: 2 },
  graphCard: { background: '#fff', borderRadius: 10, padding: '14px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  barChart: { display: 'flex', gap: 6, alignItems: 'flex-end', height: 80 },
  barItem: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  barWrap: { width: '100%', height: 60, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  bar: { width: '100%', borderRadius: '4px 4px 0 0', position: 'relative', minHeight: 4, transition: 'height 0.3s' },
  barVal: { position: 'absolute', top: -16, left: '50%', transform: 'translateX(-50%)', fontSize: 10, fontWeight: 600, color: '#1C2833' },
  barLabel: { fontSize: 9, color: '#888', textAlign: 'center' },
  commCard: { background: '#fff', borderRadius: 10, padding: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  commHeader: { display: 'flex', alignItems: 'center', gap: 10 },
  commAv: { width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, flexShrink: 0 },
  commNom: { fontSize: 13, fontWeight: 600, color: '#1C2833' },
  commSub: { fontSize: 11, color: '#888', marginTop: 2 },
  primeRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#EAF3DE', borderRadius: 8, padding: '8px 12px', marginTop: 8 },
  primeLabel: { fontSize: 12, color: '#3B6D11' },
  primeVal: { fontSize: 14, fontWeight: 700, color: '#3B6D11' },
  resultatCard: { background: '#fff', borderRadius: 10, padding: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 10 },
  resultatRow: { display: 'flex', alignItems: 'center', gap: 8 },
  resultatDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  resultatCount: { fontSize: 13, fontWeight: 600, color: '#1C2833', minWidth: 20, textAlign: 'right' },
  resultatBarWrap: { flex: 1, height: 6, background: '#f0f0f0', borderRadius: 99 },
  resultatBar: { height: 6, borderRadius: 99, transition: 'width 0.3s' },
  resultatPct: { fontSize: 11, color: '#888', minWidth: 30, textAlign: 'right' },
  empty: { textAlign: 'center', color: '#888', padding: '2rem' },
};
